import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';

@Component({
  selector: 'app-retireplan-advisory',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './retireplan-advisory.component.html',
  styleUrl: './retireplan-advisory.component.scss'
})
export class RetireplanAdvisoryComponent implements OnInit, OnDestroy {
  private jsonLdScript: any;
  constructor(private router: Router, private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document) { }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }
  nav(url: any) {
    this.router.navigateByUrl(url)
  }
injectSchemaMarkup(): void {
    const retirementSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/us-retirement-plan",
          "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/us-retirement-plan",
          "name": "US Retirement Plan – Innovative Solutions for Retirement Planning and Pricing Strategies | Kalpita Case Study",
          "description": "Kalpita Technologies developed a customized solution for a mid-sized financial advisory firm, using OCR and Azure Cognitive Services to automate data extraction, improve retirement plan cost visibility by 30%, and reduce report generation time by 50%.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".unified-head",
              ".unified-para",
              ".sub-heading"
            ]
          },
          "datePublished": "2025-01-15",
          "dateModified": "2025-01-15",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com/"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/us-retirement-plan#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/",
                "url": "https://www.kalpitatechnologies.com/",
                "name": "Home"
              }
            },
            {
              "@type": "ListItem",
              "position": 2,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/case-study",
                "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/case-study",
                "name": "Case Studies"
              }
            },
            {
              "@type": "ListItem",
              "position": 3,
              "item": {
                "@type": "WebPage",
                "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/us-retirement-plan",
                "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/us-retirement-plan",
                "name": "US Retirement Plan"
              }
            }
          ]
        },
        {
          "@type": "Article",
          "headline": "US Retirement Plan – Innovative Solutions for Retirement Planning and Pricing Strategies",
          "articleBody": "Kalpita Technologies developed a customized solution for a reputable mid-sized financial advisory firm... incorporating advanced technology such as Optical Character Recognition (OCR) and Azure Cognitive Services to achieve 30% improved data accuracy.",
          "keywords": [
            "retirement planning", "pricing strategies", "financial advisory", "OCR data extraction", "Azure Cognitive Services", "competitive benchmarking", "retirement plan costs", "AngularJS", ".NET Core", "finance advisory technology"
          ],
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
            }
          },
          "datePublished": "2025-01-15",
          "image": "https://www.kalpitatechnologies.com/assets/resources/casestudies/retire-advisory/Client_Profile_img.png",
          "articleSection": "Case Studies"
        },
        {
          "@type": "HowTo",
          "name": "How to Implement an Automated Retirement Planning and Pricing Solution",
          "description": "Step-by-step approach Kalpita Technologies used to build a data-driven retirement planning platform with OCR automation.",
          "step": [
            { "@type": "HowToStep", "name": "Identify Operational Challenges", "text": "Assess challenges including lack of cost visibility and manual extraction errors." },
            { "@type": "HowToStep", "name": "Deploy OCR and Azure", "text": "Implement OCR and Azure Cognitive Services to automate document data extraction." },
            { "@type": "HowToStep", "name": "Build Analysis Tools", "text": "Develop breakdown tools for administrative and investment fees." },
            { "@type": "HowToStep", "name": "Integrate Infrastructure", "text": "Connect with existing systems using .NET Core and SQL Server." },
            { "@type": "HowToStep", "name": "Customize Reporting", "text": "Build personalized reporting and conduct advisor training." },
            { "@type": "HowToStep", "name": "Measure Success", "text": "Validate outcomes including 30% accuracy improvement and 50% faster reporting." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How does OCR technology improve the retirement planning process?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "OCR combined with Azure Cognitive Services automates extraction, eliminating manual entry errors and increasing processing speed by 50%."
              }
            },
            {
              "@type": "Question",
              "name": "What results did the firm achieve?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The firm achieved 30% better data accuracy, 50% faster report generation, and more market-aligned pricing strategies."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
          }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(retirementSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
}
